/*
Gra "Rzut przed meczem" - port 1:1 prototypu SAVICKI-Rzut-przed-meczem_4.html.

Boisko liczymy w stałych jednostkach (900x540, a przy kontenerze węższym niż 640 px
pionowe 520x640) i skalujemy do szerokości kontenera, więc fizyka i trudność są takie
same na każdym ekranie. Stałe (grawitacja, siła rzutu, odbicia) są z prototypu -
ruszenie którejkolwiek zmienia trudność gry.

Stany: idle (zasłona przed startem) -> ready (piłka czeka, zegar stoi do pierwszego
rzutu) -> play -> buzzer (czas minął, piłka jeszcze w powietrzu - rzut przed syreną
się liczy) -> over.
*/

import { BEST_KEY, readStorage, writeStorage } from './utils';

const GRAVITY = 1650;
const AIM_POWER = 5.4;
const MAX_SPEED = 1500;
const MIN_SPEED = 260;
const TAU = Math.PI * 2;
const INK = '#1D1C1C';
const NET_COLS = 9;
const NET_ROWS = 6;

export function createGame({ el, config, onEnd }) {
    const { canvas, stage } = el;
    const ctx = canvas.getContext('2d');
    const { texts, threshold } = config;
    const round = config.round_seconds;
    // Canvas nie rozwiazuje var(), wiec krój sklepu (base/typography.scss) czytamy raz z :root.
    const fontSans =
        getComputedStyle(document.documentElement).getPropertyValue('--savicki-font-sans').trim() ||
        'Arial, sans-serif';

    const bgCanvas = document.createElement('canvas');
    const bgCtx = bgCanvas.getContext('2d');
    const ballImage = new Image();
    let ballImageReady = false;

    let W;
    let H;
    let BR;
    let floorY;
    let hoop;
    let board;
    let threeX;
    let ball;
    let nodes = [];
    let state = 'idle';
    let t0 = 0;
    let timeLeft = round;
    let pts = 0;
    let best = Number(readStorage(BEST_KEY)) || 0;
    let aim = null;
    let trail = [];
    let lastT = 0;
    let resetAt = 0;
    let touched = false;
    let scoredThis = false;
    let toastTimer;
    let seed = 7;

    const isPortrait = () => W < 600;

    // Deterministyczny los dla tła - trybuny i deski wyglądają zawsze tak samo.
    function rnd() {
        seed = (seed * 16807) % 2147483647;

        return (seed - 1) / 2147483646;
    }

    /* ---------- HUD ---------- */

    function hud() {
        el.time.textContent = timeLeft.toFixed(1).replace('.', ',');
        el.points.textContent = pts;
        el.best.textContent = best;
        el.bar.style.width = `${Math.min(100, (pts / threshold) * 100)}%`;
    }

    function toast(text) {
        el.toast.textContent = text;
        el.toast.classList.add('is-on');
        clearTimeout(toastTimer);
        toastTimer = setTimeout(() => el.toast.classList.remove('is-on'), 900);
    }

    /* ---------- piłka i siatka ---------- */

    function placeBall(keepX) {
        const minX = isPortrait() ? 46 : 70;
        const maxX = hoop.x - 150;
        const x = keepX ?? minX + Math.random() * (maxX - minX);
        const y = floorY - (isPortrait() ? 150 : 120) - Math.random() * 40;

        ball = { x, y, vx: 0, vy: 0, rot: 0, startX: x, three: x < threeX, held: true };
        touched = false;
        scoredThis = false;
        trail = [];
    }

    function initNet() {
        nodes = [];

        for (let r = 0; r < NET_ROWS; r += 1) {
            for (let c = 0; c < NET_COLS; c += 1) {
                const f = r / (NET_ROWS - 1);
                const wd = hoop.r * (1 - 0.42 * f);
                const x = hoop.x - wd + (2 * wd * c) / (NET_COLS - 1);
                const y = hoop.y + 2 + f * 52;

                nodes.push({ x, y, px: x, py: y, rx: x - hoop.x, ry: y - hoop.y, pin: r === 0 });
            }
        }
    }

    function netStep() {
        nodes.forEach((n) => {
            if (n.pin) {
                n.x = hoop.x + n.rx;
                n.y = hoop.y + n.ry;

                return;
            }

            let vx = (n.x - n.px) * 0.9;
            let vy = (n.y - n.py) * 0.9;

            n.px = n.x;
            n.py = n.y;
            // sprężyna do kształtu spoczynkowego
            vx += (hoop.x + n.rx - n.x) * 0.12;
            vy += (hoop.y + n.ry - n.y) * 0.12;
            n.x += vx;
            n.y += vy;

            const dx = n.x - ball.x;
            const dy = n.y - ball.y;
            const d = Math.hypot(dx, dy);
            const m = BR + 1.5;

            if (d < m && d > 0) {
                n.x = ball.x + (dx / d) * m;
                n.y = ball.y + (dy / d) * m;
            }
        });
    }

    /* ---------- tło (rysowane raz, przy zmianie rozmiaru) ---------- */

    function bakeBackground() {
        bgCanvas.width = canvas.width;
        bgCanvas.height = canvas.height;

        const s = canvas.width / W;
        const b = bgCtx;

        b.setTransform(s, 0, 0, s, 0, 0);

        // ściana i trybuny w nieostrości
        const wall = b.createLinearGradient(0, 0, 0, floorY);

        wall.addColorStop(0, '#D9D3CB');
        wall.addColorStop(0.55, '#ECE7E0');
        wall.addColorStop(1, '#F5F1EB');
        b.fillStyle = wall;
        b.fillRect(0, 0, W, floorY);

        seed = 11;
        b.save();
        b.filter = 'blur(9px)';

        for (let row = 0; row < 7; row += 1) {
            const y = floorY - 40 - row * 26;

            b.fillStyle = `rgba(255,255,255,${0.35 - row * 0.03})`;
            b.fillRect(-20, y, W + 40, 9);

            for (let c = 0; c < W; c += 22 + rnd() * 10) {
                b.fillStyle = `rgba(120,110,100,${0.025 + rnd() * 0.03})`;
                b.fillRect(c, y + 9, 14, 12);
            }
        }

        for (let k = 0; k < 5; k += 1) {
            const lx = W * (0.1 + k * 0.2);
            const light = b.createRadialGradient(lx, 0, 0, lx, 0, 140);

            light.addColorStop(0, 'rgba(255,255,255,.8)');
            light.addColorStop(1, 'rgba(255,255,255,0)');
            b.fillStyle = light;
            b.fillRect(lx - 140, 0, 280, 140);
        }

        b.restore();

        // parkiet
        const fy = floorY - 16;
        const floor = b.createLinearGradient(0, fy, 0, H);

        floor.addColorStop(0, '#CDAE82');
        floor.addColorStop(0.25, '#DDBF93');
        floor.addColorStop(1, '#E8CFA6');
        b.fillStyle = floor;
        b.fillRect(0, fy, W, H - fy);

        seed = 5;
        const rows = [fy, fy + 4, fy + 9, fy + 16, fy + 25, fy + 37, fy + 52, H];

        for (let i = 0; i < rows.length - 1; i += 1) {
            const y0 = rows[i];
            const y1 = rows[i + 1];
            const len = 40 + i * 30;

            b.fillStyle = 'rgba(90,60,30,.10)';
            b.fillRect(0, y0, W, 0.7);

            for (let x = -rnd() * len; x < W; x += len * (0.7 + rnd() * 0.6)) {
                b.fillStyle = `rgba(120,80,40,${0.03 + rnd() * 0.06})`;
                b.fillRect(x, y0, len, y1 - y0);
                b.fillStyle = 'rgba(90,60,30,.12)';
                b.fillRect(x, y0, 0.7, y1 - y0);
            }
        }

        // odbicie światła w lakierze
        const gloss = b.createLinearGradient(0, fy, 0, fy + 30);

        gloss.addColorStop(0, 'rgba(255,255,255,.45)');
        gloss.addColorStop(1, 'rgba(255,255,255,0)');
        b.fillStyle = gloss;
        b.fillRect(0, fy, W, 30);

        // linia rzutów za 3 pkt, namalowana na parkiecie
        const ly = floorY - 16;

        b.fillStyle = 'rgba(255,255,255,.92)';
        b.beginPath();
        b.moveTo(threeX - 2, ly);
        b.lineTo(threeX + 2, ly);
        b.lineTo(threeX + 9, H);
        b.lineTo(threeX - 5, H);
        b.closePath();
        b.fill();

        // słup z osłoną
        const px = board.x + 46;
        const pole = b.createLinearGradient(px - 9, 0, px + 9, 0);

        pole.addColorStop(0, '#F4F2EE');
        pole.addColorStop(0.5, '#FFFFFF');
        pole.addColorStop(1, '#CFCAC3');
        b.fillStyle = pole;
        b.fillRect(px - 7, board.top + 34, 14, floorY - board.top - 34);

        const pad = b.createLinearGradient(px - 12, 0, px + 12, 0);

        pad.addColorStop(0, '#E7E1D8');
        pad.addColorStop(0.5, '#F6F2EC');
        pad.addColorStop(1, '#C9C1B6');
        b.fillStyle = pad;
        b.fillRect(px - 12, floorY - 120, 24, 120);
        b.fillStyle = 'rgba(0,0,0,.08)';
        b.beginPath();
        b.ellipse(px, floorY, 30, 4, 0, 0, TAU);
        b.fill();

        // ramię
        b.strokeStyle = '#BDB6AC';
        b.lineWidth = 3;
        b.beginPath();
        b.moveTo(board.x + 4, board.top + 38);
        b.lineTo(px, board.top + 38);
        b.stroke();
        b.lineWidth = 2;
        b.beginPath();
        b.moveTo(board.x + 4, board.top + 80);
        b.lineTo(px, board.top + 38);
        b.stroke();

        // tablica ze szkła, widziana z boku
        const bw = 7;
        const glass = b.createLinearGradient(board.x - bw, 0, board.x + bw, 0);

        glass.addColorStop(0, 'rgba(210,225,228,.55)');
        glass.addColorStop(0.5, 'rgba(255,255,255,.9)');
        glass.addColorStop(1, 'rgba(180,196,200,.6)');
        b.fillStyle = glass;
        b.fillRect(board.x - 2, board.top, bw, board.bot - board.top);
        b.strokeStyle = 'rgba(29,28,28,.55)';
        b.lineWidth = 1;
        b.strokeRect(board.x - 2, board.top, bw, board.bot - board.top);
        b.fillStyle = '#2A2928';
        b.fillRect(board.x - 2, board.bot - 4, bw, 4);
    }

    /* ---------- rysowanie klatki ---------- */

    function aimVelocity() {
        let vx = (aim.x - ball.x) * AIM_POWER;
        let vy = (aim.y - ball.y) * AIM_POWER;
        const speed = Math.hypot(vx, vy);

        if (speed > MAX_SPEED) {
            vx *= MAX_SPEED / speed;
            vy *= MAX_SPEED / speed;
        }

        return { vx, vy };
    }

    function drawRing(front) {
        const gold = ctx.createLinearGradient(hoop.x - hoop.r, 0, hoop.x + hoop.r, 0);

        gold.addColorStop(0, '#8C6A36');
        gold.addColorStop(0.3, '#E6CB8F');
        gold.addColorStop(0.5, '#F7E7BF');
        gold.addColorStop(0.7, '#C9A462');
        gold.addColorStop(1, '#8C6A36');
        ctx.strokeStyle = front ? gold : '#8E6F3E';
        ctx.lineWidth = front ? 4 : 3;
        ctx.beginPath();
        ctx.ellipse(hoop.x, hoop.y, hoop.r, 7, 0, front ? 0 : Math.PI, front ? Math.PI : TAU);
        ctx.stroke();

        if (!front) {
            return;
        }

        // brylant w obręczy
        const dx = hoop.x;
        const dy = hoop.y + 7;
        const stone = ctx.createLinearGradient(dx - 5, dy - 5, dx + 5, dy + 5);

        stone.addColorStop(0, '#FFFFFF');
        stone.addColorStop(0.5, '#DCE6EA');
        stone.addColorStop(1, '#FFFFFF');
        ctx.fillStyle = stone;
        ctx.strokeStyle = '#8C6A36';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(dx, dy - 5.5);
        ctx.lineTo(dx + 5.5, dy);
        ctx.lineTo(dx, dy + 5.5);
        ctx.lineTo(dx - 5.5, dy);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = 'rgba(255,255,255,.95)';
        ctx.fillRect(dx - 1, dy - 3, 1.2, 1.2);
    }

    function drawNet() {
        const node = (r, c) => nodes[r * NET_COLS + c];

        ctx.lineCap = 'round';

        [0, 1].forEach((pass) => {
            ctx.strokeStyle = pass ? 'rgba(255,255,255,.96)' : 'rgba(40,35,30,.35)';
            ctx.lineWidth = pass ? 1.1 : 2.2;
            ctx.beginPath();

            for (let r = 0; r < NET_ROWS - 1; r += 1) {
                for (let c = 0; c < NET_COLS; c += 1) {
                    const a = node(r, c);

                    if (c < NET_COLS - 1) {
                        const b = node(r + 1, c + 1);

                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                    }

                    if (c > 0) {
                        const b = node(r + 1, c - 1);

                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                    }
                }
            }

            ctx.stroke();
        });
    }

    function drawBallAt(x, y, rot) {
        ctx.save();
        ctx.translate(x, y);
        ctx.beginPath();
        ctx.arc(0, 0, BR, 0, TAU);
        ctx.clip();

        if (ballImageReady) {
            ctx.save();
            ctx.rotate(rot);
            ctx.drawImage(ballImage, -BR * 1.02, -BR * 1.02, BR * 2.04, BR * 2.04);
            ctx.restore();
        } else {
            ctx.fillStyle = '#B98352';
            ctx.fillRect(-BR, -BR, 2 * BR, 2 * BR);
        }

        // stałe światło z góry, niezależnie od obrotu
        const light = ctx.createRadialGradient(-BR * 0.4, -BR * 0.5, BR * 0.1, 0, 0, BR * 1.05);

        light.addColorStop(0, 'rgba(255,245,230,.28)');
        light.addColorStop(0.55, 'rgba(255,255,255,0)');
        light.addColorStop(1, 'rgba(40,20,5,.45)');
        ctx.fillStyle = light;
        ctx.fillRect(-BR, -BR, 2 * BR, 2 * BR);
        ctx.restore();
    }

    function draw() {
        ctx.clearRect(0, 0, W, H);

        if (bgCanvas.width) {
            ctx.drawImage(bgCanvas, 0, 0, W, H);
        }

        // uchwyt obręczy
        ctx.fillStyle = '#9C7A45';
        ctx.fillRect(hoop.x + hoop.r - 2, hoop.y - 2, board.x - hoop.x - hoop.r + 2, 5);
        drawRing(false);

        // cień piłki na parkiecie
        const height = Math.max(0, floorY - (ball.y + BR));
        const shade = Math.max(0.05, 0.28 - height / 1400);

        ctx.fillStyle = `rgba(60,40,20,${shade})`;
        ctx.beginPath();
        ctx.ellipse(ball.x, floorY - 6, BR * (1 + height / 500), BR * 0.22, 0, 0, TAU);
        ctx.fill();

        // podgląd celowania
        if (aim && ball.held) {
            const v = aimVelocity();
            const n = isPortrait() ? 9 : 8;

            for (let k = 1; k <= n; k += 1) {
                const tt = k * 0.035;
                const px = ball.x + v.vx * tt;
                const py = ball.y + v.vy * tt + 0.5 * GRAVITY * tt * tt;

                ctx.globalAlpha = 1 - k / (n + 1);
                ctx.fillStyle = '#fff';
                ctx.strokeStyle = 'rgba(29,28,28,.6)';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.arc(px, py, 2.6, 0, TAU);
                ctx.fill();
                ctx.stroke();
            }

            ctx.globalAlpha = 1;
        }

        // smuga ruchu
        if (!ball.held) {
            for (let i = 0; i < trail.length; i += 2) {
                const [tx, ty, tr] = trail[i];

                ctx.globalAlpha = (0.05 * i) / trail.length;
                drawBallAt(tx, ty, tr);
            }

            ctx.globalAlpha = 1;
        }

        drawBallAt(ball.x, ball.y, ball.rot);
        drawNet();
        drawRing(true);

        if (ball.held && state !== 'over') {
            const label = (ball.three ? texts.ball_three : texts.ball_two).toUpperCase();

            ctx.font = `600 10px ${fontSans}`;
            ctx.textAlign = 'center';

            const w = ctx.measureText(label).width + 22;

            ctx.fillStyle = 'rgba(255,255,255,.85)';
            ctx.fillRect(ball.x - w / 2, ball.y + BR + 10, w, 20);
            ctx.fillStyle = INK;
            ctx.save();
            ctx.letterSpacing = '2px';
            ctx.fillText(label, ball.x + 1, ball.y + BR + 24);
            ctx.restore();
        }
    }

    /* ---------- układ ---------- */

    function layout() {
        const w = stage.clientWidth || 900;
        const portrait = w < 640;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        W = portrait ? 520 : 900;
        H = portrait ? 640 : 540;

        const scale = w / W;

        canvas.width = Math.round(W * scale * dpr);
        canvas.height = Math.round(H * scale * dpr);
        canvas.style.height = `${H * scale}px`;
        ctx.setTransform(scale * dpr, 0, 0, scale * dpr, 0, 0);

        BR = portrait ? 19 : 18;
        floorY = H - 46;

        const hx = portrait ? W - 118 : W - 150;
        const hy = portrait ? H * 0.34 : H * 0.36;

        hoop = { x: hx, y: hy, r: portrait ? 44 : 42 };
        board = { x: hx + hoop.r + 14, top: hy - 110, bot: hy + 22 };
        threeX = portrait ? hx - 250 : hx - 380;

        if (!ball || ball.held) {
            placeBall(null);
        }

        initNet();
        bakeBackground();
    }

    /* ---------- fizyka ---------- */

    function rim(rx, ry) {
        const dx = ball.x - rx;
        const dy = ball.y - ry;
        const d = Math.hypot(dx, dy);
        const m = BR + 2.5;

        if (d < m && d > 0) {
            const nx = dx / d;
            const ny = dy / d;

            ball.x = rx + nx * m;
            ball.y = ry + ny * m;

            const vn = ball.vx * nx + ball.vy * ny;

            if (vn < 0) {
                ball.vx -= 1.55 * vn * nx;
                ball.vy -= 1.55 * vn * ny;
                ball.vx *= 0.9;
                ball.vy *= 0.9;
            }

            touched = true;
        }
    }

    function score() {
        const add = ball.three ? 3 : 2;

        scoredThis = true;
        pts += add;
        toast(
            (add === 3 ? texts.toast_three : texts.toast_two) + (touched ? '' : texts.toast_clean)
        );
        hud();
        resetAt = Math.min(resetAt, performance.now() + 700);
    }

    function end() {
        state = 'over';
        timeLeft = 0;
        ball.held = true;

        if (pts > best) {
            best = pts;
            writeStorage(BEST_KEY, String(best));
        }

        hud();
        onEnd(pts);
    }

    function finishShot() {
        if (!scoredThis && state !== 'over') {
            toast(texts.toast_miss);
        }

        if (state === 'buzzer') {
            end();

            return;
        }

        // Po trafieniu nowe miejsce; po pudle pół na pół to samo albo nowe.
        placeBall(scoredThis ? null : ball.startX);

        if (!scoredThis && Math.random() < 0.5) {
            placeBall(null);
        }
    }

    function step(dt) {
        const b = ball;

        if (b.held) {
            return;
        }

        const sub = 4;
        const h = dt / sub;

        for (let s = 0; s < sub; s += 1) {
            const py = b.y;

            b.vy += GRAVITY * h;
            b.x += b.vx * h;
            b.y += b.vy * h;
            b.rot += ((b.vx * h) / BR) * 0.6;

            // kosz: piłka przecina linię obręczy z góry, między krawędziami
            if (
                !scoredThis &&
                py < hoop.y &&
                b.y >= hoop.y &&
                b.vy > 0 &&
                b.x > hoop.x - hoop.r + BR * 0.55 &&
                b.x < hoop.x + hoop.r - BR * 0.55
            ) {
                score();
            }

            // krawędzie obręczy
            rim(hoop.x - hoop.r, hoop.y);
            rim(hoop.x + hoop.r, hoop.y);

            // tablica
            if (b.y > board.top && b.y < board.bot && b.x + BR > board.x && b.x < board.x + 6) {
                b.x = board.x - BR;
                b.vx = -Math.abs(b.vx) * 0.62;
                touched = true;
            }

            if (
                b.x > board.x - 2 &&
                b.x < board.x + 4 &&
                b.y + BR > board.top &&
                b.y < board.top &&
                b.vy > 0
            ) {
                b.y = board.top - BR;
                b.vy = -b.vy * 0.5;
            }

            // parkiet
            if (b.y + BR > floorY) {
                b.y = floorY - BR;
                b.vy = -b.vy * 0.55;
                b.vx *= 0.8;

                if (Math.abs(b.vy) < 60) {
                    b.vy = 0;
                }
            }
        }

        trail.push([b.x, b.y, b.rot]);

        if (trail.length > 8) {
            trail.shift();
        }

        // siatka hamuje piłkę
        if (b.y > hoop.y && b.y < hoop.y + 55 && Math.abs(b.x - hoop.x) < hoop.r) {
            b.vy *= 0.97;
            b.vx *= 0.95;
        }

        const resting = b.y + BR >= floorY - 1 && Math.abs(b.vy) < 1 && Math.abs(b.vx) < 30;

        if (b.x < -60 || b.x > W + 60 || performance.now() > resetAt || resting) {
            finishShot();
        }
    }

    /* ---------- sterowanie ---------- */

    function pointerPosition(e) {
        const rect = canvas.getBoundingClientRect();

        return {
            x: ((e.clientX - rect.left) * W) / rect.width,
            y: ((e.clientY - rect.top) * H) / rect.height,
        };
    }

    function shoot(v) {
        if (state === 'ready') {
            state = 'play';
            t0 = performance.now();
        }

        ball.held = false;
        ball.vx = v.vx;
        ball.vy = v.vy;
        resetAt = performance.now() + 3200;
    }

    canvas.addEventListener('pointerdown', (e) => {
        if ((state !== 'play' && state !== 'ready') || !ball.held) {
            return;
        }

        const p = pointerPosition(e);

        // chwytamy tylko w okolicy piłki
        if (Math.hypot(p.x - ball.x, p.y - ball.y) > BR * 4.5) {
            return;
        }

        aim = p;
        canvas.setPointerCapture(e.pointerId);
        e.preventDefault();
    });

    canvas.addEventListener('pointermove', (e) => {
        if (aim) {
            aim = pointerPosition(e);
            e.preventDefault();
        }
    });

    canvas.addEventListener('pointerup', (e) => {
        if (!aim) {
            return;
        }

        aim = pointerPosition(e);

        const v = aimVelocity();

        aim = null;

        if (Math.hypot(v.vx, v.vy) < MIN_SPEED) {
            return;
        }

        shoot(v);
    });

    canvas.addEventListener('pointercancel', () => {
        aim = null;
    });

    /* ---------- pętla ---------- */

    function loop(t) {
        const dt = Math.min(0.033, (t - (lastT || t)) / 1000);

        lastT = t;

        if (state === 'play') {
            timeLeft = Math.max(0, round - (t - t0) / 1000);

            if (timeLeft <= 0) {
                if (!ball.held) {
                    state = 'buzzer';
                    toast(texts.toast_buzzer);
                } else {
                    end();
                }
            }

            hud();
        }

        if (state === 'play' || state === 'buzzer' || state === 'ready') {
            step(dt);
        }

        netStep();
        draw();
        requestAnimationFrame(loop);
    }

    /* ---------- start ---------- */

    function start() {
        pts = 0;
        timeLeft = round;
        state = 'ready';
        placeBall(null);
        hud();
    }

    ballImage.onload = () => {
        ballImageReady = true;
    };
    ballImage.src = config.ball_image;

    let lastWidth = stage.clientWidth;

    window.addEventListener('resize', () => {
        const w = stage.clientWidth;

        if (Math.abs(w - lastWidth) < 2) {
            return;
        }

        lastWidth = w;
        layout();
    });

    layout();
    hud();
    requestAnimationFrame(loop);

    return { start };
}
